import { tokenBanda, type RangEditie } from '../utils/rang';

/**
 * Capul paginii „Puncte și premii": SOLDUL sus, apoi distanta pana la urmatorul nivel.
 * DOUA CANTITATI, NICIODATA O SINGURA BARA.
 *
 * 🔴 Soldul (`available`) decide ce isi permite scoala acum; nivelul vine din `earned` (totalul
 * castigat in editie) si il decide `calculeazaRang`. Sunt independente si se deseneaza separat: pana
 * la 2026-09-06 bara era calculata din sold, deci o scoala care isi lua o recompensa vedea bara SCAZAND
 * — ecranul ii spunea ca a fost retrogradata fiindca a cheltuit.
 *
 * ⚠️ Nicio propozitie de aici nu spune „nivelul nu scade niciodata". `earned` e recalculat la fiecare
 * citire, iar o activitate respinsa dupa aprobare il poate micsora. Ce se afirma e mai ingust:
 * **cheltuiala nu iti coboara nivelul.**
 *
 * Item 15 (2026-09-21): soldul a urcat primul, la cererea owner-ului, iar scara de niveluri a iesit de
 * aici — traieste acum o singura data, in lista unica de pe pagina (`NivelPremiu`).
 */
interface ProgresRangProps {
    /** Pozitia scolii, calculata o singura data in pagina, din `earned`. */
    rang: RangEditie;
    /** Soldul disponibil. NU intra in calculul nivelului. */
    available: number;
}

/** Cifrele se scriu cu separatorul romanesc, si mereu `tabular-nums`: altfel danseaza la refresh. */
function cifra(n: number): string {
    return n.toLocaleString('ro-RO');
}

export function ProgresRang({ rang, available }: ProgresRangProps) {
    return (
        <section
            className="flex min-w-0 flex-col gap-2 rounded-lg border border-hairline bg-card px-4 py-3 text-cerneala"
            aria-label="Soldul și nivelul școlii"
        >
            <div>
                <p className="m-0 text-small">Sold disponibil</p>
                <p className="m-0 font-heading text-fluid-2xl font-bold tabular-nums">{cifra(available)} puncte</p>
            </div>
            <Distanta rang={rang} />
            {/* Singura afirmatie pe care datele o sustin. */}
            <p className="m-0 text-xs opacity-80">Cheltuiala nu îți coboară nivelul.</p>
        </section>
    );
}

/**
 * Trei cazuri, si al treilea e cel usor de uitat:
 *
 * 1. exista un nivel urmator  -> cat mai lipseste pana la el, plus bara segmentului;
 * 2. scoala e pe ULTIMUL nivel -> nu exista distanta;
 * 3. nu exista niciun nivel introdus -> nu se inventeaza nimic (mesajul „nu au fost încă publicate"
 *    ramane o singura data, in sectiunea de premii a paginii).
 */
function Distanta({ rang }: { rang: RangEditie }) {
    if (rang.niveluri.length === 0) return null;

    if (rang.laVarf) {
        return (
            <p className="m-0 text-small">
                Ai atins ultimul nivel: <span className="font-semibold">{rang.curent?.name}</span>
            </p>
        );
    }

    return (
        <div>
            <p className="m-0 text-small">
                <span className="tabular-nums">Mai ai {cifra(rang.distanta)} puncte</span> până la{' '}
                <span className="font-semibold">{rang.urmator?.name}</span>
            </p>
            <BaraSegment rang={rang} />
        </div>
    );
}

/**
 * Progresul in SEGMENTUL curent, nu din zero pana la tinta: o bara `earned / costUrmator` ar arata
 * aproape plina pentru orice scoala avansata, indiferent cat mai are de mers.
 *
 * Se alimenteaza din `earned`. Nu are cum sa scada la o revendicare, si asta e tot rostul.
 */
function BaraSegment({ rang }: { rang: RangEditie }) {
    const de_la = rang.curent?.pointsCost ?? 0;
    const pana_la = rang.urmator?.pointsCost ?? 0;
    const lungime = pana_la - de_la;
    const procent = lungime > 0 ? Math.min(100, Math.max(0, Math.round(((pana_la - rang.distanta - de_la) / lungime) * 100))) : 0;

    return (
        <div className="mt-2">
            <div
                className="h-2 w-full overflow-hidden rounded-full bg-lilac/30"
                role="progressbar"
                aria-valuenow={procent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Progres către ${rang.urmator?.name ?? 'următorul nivel'}`}
            >
                <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${procent}%`, backgroundColor: `var(${tokenBanda(rang.niveluri.length - 2, rang.niveluri.length)})` }}
                />
            </div>
        </div>
    );
}
